/**
 * JWT utility functions for handling access tokens
 */

export interface JWTPayload {
  exp?: number // Expiration time (seconds since epoch)
  iat?: number // Issued at time (seconds since epoch)
  sub?: string // Subject (user ID)
  jti?: string // JWT ID (session ID)
  iss?: string // Issuer
  [key: string]: unknown
}

/**
 * Decodes a JWT token payload without verification
 * Note: This is for client-side use only to read expiration times
 * Server-side verification still required for security
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) {
      return null
    }

    const payload = parts[1]
    // Add padding if needed
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4)

    const decoded = atob(paddedPayload)
    return JSON.parse(decoded) as JWTPayload
  } catch (error) {
    console.error("Failed to decode JWT:", error)
    return null
  }
}

/**
 * Gets the expiration time from a JWT token
 */
export function getJWTExpiration(token: string): Date | null {
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return null
  }

  // Convert from seconds to milliseconds
  return new Date(payload.exp * 1000)
}

/**
 * Checks if a JWT token is expired
 */
export function isJWTExpired(token: string): boolean {
  const expiration = getJWTExpiration(token)
  if (!expiration) {
    return true // Treat invalid tokens as expired
  }

  return expiration <= new Date()
}

/**
 * Checks if a JWT token will expire within the given number of minutes
 */
export function isJWTExpiringWithin(token: string, minutes: number): boolean {
  const expiration = getJWTExpiration(token)
  if (!expiration) {
    return true // Treat invalid tokens as expiring
  }

  const now = new Date()
  const expirationBuffer = new Date(now.getTime() + minutes * 60 * 1000)

  return expiration <= expirationBuffer
}

/**
 * Gets the remaining time until JWT expiration in milliseconds
 */
export function getJWTRemainingTime(token: string): number {
  const expiration = getJWTExpiration(token)
  if (!expiration) {
    return 0
  }

  const remaining = expiration.getTime() - new Date().getTime()
  return Math.max(0, remaining)
}
